import axiosInstance from "@src/axios/axiosInstance";
import { Subscribable } from "@src/context/Subscribable";
import { getDb } from "@src/db/EpisodesDb";
import type { IEpisode, IWatchEpisode, TEpisode } from "@src/types/AppTypes";
import React, { createContext, type RefObject } from "react";
import Cookies from "js-cookie";

class VideoTitle extends Subscribable<() => void> {
  title: string;

  constructor(title: string) {
    super();
    this.title = title;
  }

  getTitle = () => {
    return this.title;
  };

  setTitle = (value: string) => {
    this.title = value;
    this.notify();
  };
}

class EpisodeItem extends Subscribable<() => void> {
  episode: (IEpisode & TEpisode) | null = null;

  setNextEpisode = (value: (IEpisode & TEpisode) | null) => {
    this.episode = value;
    this.notify();
  };

  getNextExpisode = () => {
    return this.episode;
  };
}

class Popup extends Subscribable<() => void> {
  popup: boolean = true;

  setPopup = (value: boolean) => {
    this.popup = value;
    this.notify();
  };

  getPopup = () => {
    return this.popup;
  };
}

class ShowPurchasePopup extends Subscribable<() => void> {
  showPopup: boolean = false;

  getShowPopup = () => {
    return this.showPopup;
  };
  setShowPopup = (value: boolean) => {
    this.showPopup = value;
    this.notify();
  };
}

class ExtraData extends Subscribable<() => void> {
  slug: string = "";
  trailer: string = "";
  full_episode: string = "";
  content_type: string = "";
  total_comments: number = 0;
  total_likes: number = 0;
  total_saves: number = 0;
  is_liked: boolean = false;
  is_saved: boolean = false;
  showPurchasePopup: ShowPurchasePopup = new ShowPurchasePopup();

  setExtraData = (item: IWatchEpisode) => {
    this.slug = item.slug;
    this.trailer = item.trailer;
    this.full_episode = item.full_episode;
    this.content_type = item.content_type;
    this.total_comments = item.total_comments;
    this.total_likes = item.total_likes;
    this.total_saves = item.total_saves;
    this.is_liked = item.is_liked;
    this.is_saved = item.is_saved;
    this.notify();
  };

  resetExtraData = () => {
    this.slug = "";
    this.trailer = "";
    this.full_episode = "";
    this.content_type = "";
    this.total_comments = 0;
    this.total_likes = 0;
    this.total_saves = 0;
    this.is_liked = false;
    this.is_saved = false;
    this.notify();
  };

  getExtraData = () => {
    return {
      slug: this.slug,
      trailer: this.trailer,
      full_episode: this.full_episode,
      content_type: this.content_type,
      total_comments: this.total_comments,
      total_likes: this.total_likes,
      total_saves: this.total_saves,
      is_liked: this.is_liked,
      is_saved: this.is_saved,
    };
  };
}

export class VideoPlayerManager extends Subscribable<() => void> {
  isIphone: boolean = false;
  title: VideoTitle = new VideoTitle("");
  item: (IEpisode & TEpisode) | null = null;
  popup: Popup = new Popup();
  nextEpisode: EpisodeItem = new EpisodeItem();
  prevEpisode: EpisodeItem = new EpisodeItem();
  extraData: ExtraData = new ExtraData(); //stores the data for likes, comments etc.
  currentSeasonData: IEpisode[] = [];
  isListFetched: boolean = false;
  databaseVideos: (IEpisode & TEpisode)[] = [];
  ref: RefObject<HTMLDivElement | HTMLVideoElement> = React.createRef();

  constructor() {
    super();
    this.title = new VideoTitle("");
    const item = JSON.parse(localStorage.getItem("purchasedVideo") || "{}");
    if (typeof item === "object" && Object.keys(item).length) {
      this.setItem(item);
    } else {
      this.setItem(null);
    }
  }

  getDownloadedVideos = async () => {
    try {
      const res = await getDb()?.episodes?.toArray();
      if (res) {
        this.databaseVideos = res;
      }
    } catch (e) {
      console.error("Unable to fetch downloaded videos");
    }
  };

  updateVideoProgress = async (itemId: number, currentTime: number) => {
    try {
      await axiosInstance.post<IEpisode>("/episodes/update_progress", {
        episode_id: itemId,
        total: currentTime,
      });
    } catch (err) {
      console.error("Unable to update video progress");
    }
  };

  setIsIphone = (value: boolean) => {
    this.isIphone = value;
  };

  onMount = async (item: IEpisode & TEpisode) => {
    try {
      if (!this.databaseVideos.length) {
        await this.getDownloadedVideos();
      }
      this.item = item;
      localStorage.setItem(
        "purchasedVideo",
        JSON.stringify({ ...item, blob: null }),
      );
      const isPurchasedOrRented = item.is_purchased || item.is_rented;
      if (this.databaseVideos.length) {
        if (isPurchasedOrRented && item.downloaded) {
          this.item.full_episode =
            `/${Cookies.get("UIDT")}/` + "testm3u8/" + this.item.id;
        } else if (!isPurchasedOrRented) {
          this.item.full_episode = item.trailer;
        }
      }
      const res = await axiosInstance.post<IWatchEpisode>(
        "/episodes/" + item.slug + "/watch_episode",
        null,
        { withCredentials: true },
      );
      if (res) {
        if (res.status) {
          this.extraData.notify();
          if (this.item) {
            this.extraData.content_type = res.data.content_type;
            this.extraData.full_episode = res.data.full_episode;
            this.extraData.is_liked = res.data.is_liked;
            this.extraData.is_saved = res.data.is_saved;
            this.extraData.slug = res.data.slug;
            this.extraData.total_comments = res.data.total_comments;
            this.extraData.total_saves = res.data.total_saves;
            this.extraData.total_likes = res.data.total_likes;
            this.extraData.trailer = res.data.trailer;
            this.extraData.notify();
          }
        }
      }
      this.notify();
      if (this.isListFetched) {
        this.updateNextAndPreviousEpisodes(item.id);
      } else {
        await this.getList(item);
      }
    } catch (e) {
      console.error("Failed on mount");
    }
  };

  updateNextAndPreviousEpisodes = (id: number) => {
    if (!this.currentSeasonData.length) return;
    const currentIndex = this.currentSeasonData.findIndex((v) => v.id === id);
    if (currentIndex > -1 && currentIndex + 1 < this.currentSeasonData.length) {
      this.nextEpisode.setNextEpisode(this.currentSeasonData[currentIndex + 1]);
    } else {
      this.nextEpisode.setNextEpisode(null);
    }

    if (currentIndex > -1 && currentIndex - 1 > -1) {
      this.prevEpisode.setNextEpisode(this.currentSeasonData[currentIndex - 1]);
    } else {
      this.prevEpisode.setNextEpisode(null);
    }
  };

  getList = async (item: IEpisode) => {
    try {
      const res = await axiosInstance.get<{ episodes: IEpisode[] }>(
        "/episodes?number_of_season=" +
          item.number_of_season +
          "&user_id=64&full_episode=true",
      );
      if (res) {
        this.isListFetched = true;
        this.currentSeasonData = res.data.episodes
          .sort((a, b) => a.number_of_episode - b.number_of_episode)
          .map((item) => {
            return {
              ...item,
              downloaded: this.databaseVideos.some((c) => c.id === item.id),
            };
          });
        this.updateNextAndPreviousEpisodes(item.id);
      }
    } catch (e) {
      console.error("Unable to get list");
    }
  };

  getItem = () => {
    return this.item;
  };

  setItem = async (item: (IEpisode & TEpisode) | null) => {
    this.title.setTitle(
      "S" +
        item?.number_of_season +
        " - Episode " +
        item?.number_of_episode +
        " . " +
        item?.title,
    );
    if (item) {
      this.item = item;
      if (item.is_purchased || item.is_rented) {
        await this.onMount({ ...item, full_episode: item.full_episode });
      } else {
        await this.onMount({ ...item, full_episode: item.trailer });
      }
    } else {
      this.item = null;
    }
  };

  onNextEpisodeClick = async () => {
    if (this.nextEpisode.episode) {
      localStorage.setItem("vu", this.nextEpisode.episode.full_episode);
      localStorage.setItem("vuct", this.nextEpisode.episode.content_type);
      this.title.setTitle(
        "S" +
          this.nextEpisode.episode?.number_of_season +
          " - Episode " +
          this.nextEpisode?.episode?.number_of_episode +
          " . " +
          this.nextEpisode.episode.title,
      );
      await this.onMount(this.nextEpisode.episode);
    }
  };

  onPrevEpisodeClick = async () => {
    if (this.prevEpisode.episode) {
      localStorage.setItem("vu", this.prevEpisode.episode.full_episode);
      localStorage.setItem("vuct", this.prevEpisode.episode.content_type);
      this.title.setTitle(
        "S" +
          this.prevEpisode.episode?.number_of_season +
          " - Episode " +
          this.prevEpisode?.episode.number_of_episode +
          " . " +
          this.prevEpisode.episode.title,
      );
      await this.onMount(this.prevEpisode.episode);
    }
  };

  destroy = () => {
    localStorage.removeItem("purchasedVideo");
    localStorage.removeItem("vu");
    localStorage.removeItem("vuct");
    this.isListFetched = false;
    this.setItem(null);
    this.nextEpisode.setNextEpisode(null);
    this.prevEpisode.setNextEpisode(null);
    this.currentSeasonData = [];
    for (const listener of this.listeners) {
      listener();
    }
    this.listeners = new Set();
    this.extraData.resetExtraData();
  };
}

export const VideoPlayerManagerContext =
  createContext<VideoPlayerManager | null>(null);
